﻿import {isNullOrEmpty, StringNamedValue} from "@2d-front-monorepo/core";
import React, {JSX, ReactNode, SyntheticEvent, useCallback, useMemo, useRef, useState} from "react";
import Icon from "../Icon";
import {findMatchRange, textMatches} from "../helpers/textSearch";
import {HtmlRequiredControlProps} from "./HtmlControlProps";
import LabeledControl from "./LabeledControl";
import {useLabeledControl} from "./LabeledControlData";
import Select from "./Select";

declare module "react" {
	namespace JSX {
		interface IntrinsicElements {
			selectedcontent: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
		}
	}
}

// Typed here instead of via @types/node: only NODE_ENV is read, and Parcel replaces it at build time.
declare const process: { env: { NODE_ENV?: string } };

// Evaluated once: browsers without Customizable Select render the plain <Select> instead.
const supportsBaseSelect = typeof CSS !== "undefined" && CSS.supports("appearance", "base-select");

// React's dev-only DOM-nesting validator predates Customizable Select, so it flags our (valid)
// button/div/span children of <select>/<option>. There is no opt-out; filter just that warning.
// Parcel inlines NODE_ENV as "production" on release builds, so this whole block is stripped there.
// The `typeof` guard is for everywhere else: `process` is a bundler's gift, and a bare reference
// is a ReferenceError under anything that serves this module as-is (the design-library playground
// did exactly that). No bundler means no production build either, so the filter belongs on.
if ((typeof process === "undefined" || process.env.NODE_ENV !== "production") && supportsBaseSelect) {
	const original = console.error;
	console.error = function (...args: unknown[]): void {
		if (typeof args[0] === "string" && args[0].startsWith("In HTML, %s cannot be a child of <%s>") &&
			(args[2] === "select" || args[2] === "option")) {
			return;
		}

		original.apply(console, args);
	};
}

export interface SelectBoxItem extends StringNamedValue {
	/** Kept in the DOM (so select.value stays valid and selectedcontent renders) but not shown in the picker. */
	pickerHidden?: boolean;
}

export interface SelectBoxProps extends Omit<HtmlRequiredControlProps<HTMLSelectElement>, "value"> {
	items: SelectBoxItem[];
	placeholder?: string;
	value?: string;
	/** Clear (X) button that returns the select to the placeholder. Requires a placeholder. */
	resettable?: boolean;
	/** Render a find input at the top of the picker. Filters locally unless onFindChange is given.
	 *  A number is a threshold instead of a switch: the input appears only once `items` reaches it,
	 *  so a short list isn't given a search box it doesn't need — ignored (always shown) with
	 *  onFindChange, where `items` is a result page and not the whole list. */
	find?: boolean | number;
	/** Controlled find: SelectBox does no local filtering, the parent updates `items`/`pickerMessage`. */
	onFindChange?: (query: string) => void;
	/** Row rendered under the find input (loading / min-chars / no-results / error). */
	pickerMessage?: ReactNode;
	/** Fired (possibly repeatedly) while the picker is open; consumers must be idempotent. */
	onPickerOpen?: () => void;
	resetLabel?: string;
	findLabel?: string;
	noResultsMessage?: string;
}

function setSelectValue(select: HTMLSelectElement, value: string): void {
	// Native prototype setter + bubbled event so React's onChange fires for controlled parents.
	Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")!.set!.call(select, value);
	select.dispatchEvent(new Event("change", {bubbles: true}));
}

function highlightLabel(label: string, query: string): ReactNode {
	const range = query === "" ? null : findMatchRange(label, query);

	if (range == null) {
		return label;
	}

	return <>{label.slice(0, range.start)}
		<mark>{label.slice(range.start, range.end)}</mark>
		{label.slice(range.end)}</>;
}

export default function SelectBox(props: SelectBoxProps): JSX.Element {
	const {
		label,
		name,
		value,
		id,
		column,
		disabled,
		hidden,
		onChange,
		onBlur,
		onClick,
		ref,

		required,
		hint,
		error,
		inlineError,

		items,
		placeholder,
		resettable,
		find,
		onFindChange,
		pickerMessage,
		onPickerOpen,
		resetLabel,
		findLabel,
		noResultsMessage
	} = props;

	const {componentId, ariaRef} = useLabeledControl(id, hint, error);
	const internalRef = useRef<HTMLSelectElement>(null);
	const selectRef = ref ?? internalRef;
	const findRef = useRef<HTMLInputElement>(null);
	const [query, setQuery] = useState("");
	const controlled = onChange != null;
	// Uncontrolled reset-button visibility; controlled mode reads the value prop directly.
	const [internalHasValue, setInternalHasValue] = useState(!isNullOrEmpty(value));

	const hasPlaceholder = !isNullOrEmpty(placeholder);
	// A number means "only worth a search box from this many items on"; a boolean forces it. The
	// threshold only makes sense over a known, complete list: with controlled find `items` is the
	// current result page, which shrinks as the query narrows.
	const showFind = find === true || (typeof find === "number" && (onFindChange != null || items.length >= find));
	const localFind = showFind && onFindChange == null;

	const itemsWithPlaceholder = useMemo(() => {
		if (!hasPlaceholder) {
			return items;
		}

		return [{label: placeholder!, value: "", pickerHidden: resettable === true}, ...items];
	}, [items, placeholder, hasPlaceholder, resettable]);

	const visibleValues = useMemo(() => {
		return itemsWithPlaceholder
			.filter((item) => item.pickerHidden !== true && (!localFind || textMatches(item.label, query)))
			.map((item) => item.value);
	}, [itemsWithPlaceholder, localFind, query]);

	const focusOption = useCallback(function (optionValue: string | undefined): void {
		if (optionValue == null) {
			return;
		}

		selectRef.current?.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(optionValue)}"]`)?.focus();
	}, [selectRef]);

	const handleChange = useCallback(function (e: SyntheticEvent<HTMLSelectElement>): void {
		setInternalHasValue(!isNullOrEmpty(e.currentTarget.value));
		onChange?.(e);
	}, [onChange]);

	const handleFindChange = useCallback(function (e: SyntheticEvent<HTMLInputElement>): void {
		setQuery(e.currentTarget.value);
		onFindChange?.(e.currentTarget.value);
	}, [onFindChange]);

	const updateQuery = useCallback(function (updated: string): void {
		setQuery(updated);
		onFindChange?.(updated);
		findRef.current?.focus();
	}, [onFindChange]);

	// The picker has no close API and fires no toggle event (Chrome 150), so committing from the find
	// input focuses the option instead: the browser then handles Enter/click commit-and-close natively.
	const handleFindKeyDown = useCallback(function (e: React.KeyboardEvent<HTMLInputElement>): void {
		if (e.key === "ArrowDown" || e.key === "Enter") {
			e.preventDefault();
			const selected = selectRef.current?.value;
			focusOption(selected != null && visibleValues.includes(selected) && e.key === "ArrowDown" ? selected : visibleValues[0]);
		}
	}, [selectRef, focusOption, visibleValues]);

	// Keys pressed while an option has focus: ArrowUp on the first visible option returns to the find
	// input; printable keys/Backspace are redirected to it (native typeahead would swallow them).
	const handleSelectKeyDown = useCallback(function (e: React.KeyboardEvent<HTMLSelectElement>): void {
		if (!showFind || !(e.target instanceof HTMLOptionElement)) {
			return;
		}

		if (e.key === "ArrowUp" && e.target.value === visibleValues[0]) {
			e.preventDefault();
			findRef.current?.focus();
		} else if (e.key === "Backspace") {
			e.preventDefault();
			updateQuery(query.slice(0, -1));
		} else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
			e.preventDefault();
			updateQuery(query + e.key);
		}
	}, [showFind, visibleValues, query, updateQuery]);

	const checkPickerOpen = useCallback(function (): void {
		if (onPickerOpen == null) {
			return;
		}

		requestAnimationFrame(() => {
			if (selectRef.current?.matches(":open")) {
				onPickerOpen();
			}
		});
	}, [onPickerOpen, selectRef]);

	const handleReset = useCallback(function (): void {
		const select = selectRef.current;

		if (select == null) {
			return;
		}

		setSelectValue(select, "");
		select.focus();
	}, [selectRef]);

	if (hidden) {
		return <></>;
	}

	if (!supportsBaseSelect) {
		// ponytail: fallback IS the old control — no find/reset UI, exactly today's behavior
		return <Select label={label} name={name} items={items} placeholder={placeholder} value={value} id={id} column={column}
		               disabled={disabled} onChange={onChange} onBlur={onBlur} onClick={onClick} ref={ref}
		               required={required} hint={hint} error={error} inlineError={inlineError}/>;
	}

	const hasValue = value !== undefined && controlled ? !isNullOrEmpty(value) : internalHasValue;
	const showReset = resettable === true && hasPlaceholder && hasValue && disabled !== true;
	const message = pickerMessage ?? (localFind && query !== "" && visibleValues.length === 0 ? (noResultsMessage ?? "No results") : null);

	return <LabeledControl label={label} type="selectbox" id={componentId} required={required} column={column} error={error} inlineError={inlineError} disabled={disabled}
	                       clickable={disabled && onClick != null}
	                       hint={hint} hidden={hidden} ariaRef={ariaRef}>
		<select
			ref={selectRef}
			className="selectbox"
			id={componentId}
			onChange={handleChange}
			onBlur={onBlur}
			onClick={onClick}
			onKeyDown={showFind ? handleSelectKeyDown : undefined}
			onPointerDown={onPickerOpen != null ? checkPickerOpen : undefined}
			onKeyDownCapture={onPickerOpen != null ? checkPickerOpen : undefined}
			name={name}
			value={controlled ? value : undefined}
			defaultValue={controlled ? undefined : value}
			required={required}
			aria-describedby={ariaRef.current?.describedBy}
			aria-invalid={ariaRef.current?.invalid}
			aria-required={required}
			disabled={disabled}
			tabIndex={disabled ? -1 : undefined}>
			<button type="button">
				<selectedcontent></selectedcontent>
			</button>
			{showFind && <div className="selectbox__find">
                <input ref={findRef} id={componentId + "-find"} type="text" value={query} autoComplete="off"
                       aria-label={findLabel ?? "Find"} placeholder={findLabel ?? "Find"}
                       onChange={handleFindChange} onKeyDown={handleFindKeyDown}/>
            </div>}
			{message != null && <div className="selectbox__message">{message}</div>}
			{itemsWithPlaceholder.map((item, index) =>
				<option key={index} value={item.value}
				        className={item.pickerHidden === true || !visibleValues.includes(item.value) ? "selectbox__option--hidden" : undefined}>
					{/* Single span: options are display:flex in base-select, so split text nodes would gap apart. */}
					<span>{showFind ? highlightLabel(item.label, query) : item.label}</span>
				</option>)}
		</select>
		{showReset && <button type="button" className="selectbox__reset" aria-label={resetLabel ?? "Clear selection"} onClick={handleReset}>
            <Icon icon="selectbox-clear"/>
        </button>}
	</LabeledControl>;
};
